import React, { useEffect, useMemo, useState } from 'react'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as IconWarningOutline } from '@toasttab/shared/src/assets/svg/icon-warning-outline-black.svg'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIcon } from '@toasttab/shared/src/assets/svg/icon-close.svg'
import { useI18next } from '@toasttab/shared/src/hooks/useI18next'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import Sites from '@toasttab/shared/src/data/Sites.json'
import { normalizeRegion } from '@toasttab/shared/src/utils/siteUtils'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'

interface RegionSwitcherInfoBannerProps {
  packageName: string
  siteId: number
  localizedEntries?: {
    siteId: number
    language: string
    slug?: string
    slugOverride?: string
    disabledRegionalSwitcherModal?: boolean
  }[]
}

const normalizeLang = (lang = '') => lang.toLowerCase().split('-')[0]
const normalizeCountryCode = (lang = '') =>
  lang.toLowerCase().split('-')[1] || lang.toLowerCase()

const RegionSwitcherInfoBanner: React.FC<RegionSwitcherInfoBannerProps> = ({
  packageName,
  siteId,
  localizedEntries = []
}) => {
  const { setActiveRegionSwitcherInfoBanner } = usePageContext()
  const { t } = useI18next()
  const [isUserCountrySupported, setIsUserCountrySupported] = useState(false)
  const [isUserLangSupported, setIsUserLangSupported] = useState(false)
  const [showRegionBanner, setShowRegionBanner] = useState(false)
  const [regionData, setRegionData] = useState({
    countryCode: '',
    regionPreference: ''
  })
  const { geoData, loading } = useGeoLocation()

  const browserLang = useMemo(
    () =>
      typeof navigator === 'undefined'
        ? 'en'
        : navigator.language?.split('-')[0]?.toLowerCase() || 'en',
    []
  )

  const activeSite = useMemo(
    () => Sites.sites.find((site) => site.siteId === siteId),
    [siteId]
  )

  useEffect(() => {
    if (geoData) {
      setRegionData({
        countryCode: geoData.country.iso_code,
        regionPreference: geoData.regionPreference.iso_code
      })
    }
  }, [geoData, loading])

  useEffect(() => {
    const userLangCookie = _helpers.getCookie('_userLanguage')
    if (!userLangCookie && browserLang) {
      _helpers.createCookie('_userLanguage', browserLang, 365, '/')
    }
  }, [browserLang])

  useEffect(() => {
    if (
      !regionData.countryCode ||
      localizedEntries.some((entry: any) => entry.disabledRegionalSwitcherModal)
    ) {
      return
    }

    const userLangCookie = _helpers.getCookie('_userLanguage')
    const userCountry = regionData.countryCode?.toUpperCase()
    const preferredRegion = regionData.regionPreference?.toUpperCase()
    const doesRegionHaveSupportedContent = localizedEntries.some((entry: any) =>
      Sites.sites.some(
        (site) =>
          site.siteId === entry.siteId &&
          normalizeRegion(site.countryCode) ===
            normalizeRegion(preferredRegion || userCountry)
      )
    )

    const doesLanguageHaveSupportedContent = localizedEntries.some(
      (entry: any) =>
        normalizeLang(entry.language) === normalizeLang(userLangCookie) &&
        (normalizeCountryCode(entry.language) ===
          normalizeCountryCode(userLangCookie) ||
          normalizeCountryCode(entry.language) ===
            normalizeCountryCode(userCountry))
    )
    setIsUserCountrySupported(doesRegionHaveSupportedContent)
    setIsUserLangSupported(doesLanguageHaveSupportedContent)

    const showBanner =
      (!doesLanguageHaveSupportedContent || !doesRegionHaveSupportedContent) &&
      sessionStorage.getItem('regionBannerClosed') !== 'true'

    setShowRegionBanner(showBanner)
    setActiveRegionSwitcherInfoBanner(showBanner)
  }, [
    regionData,
    activeSite,
    localizedEntries,
    geoData,
    setActiveRegionSwitcherInfoBanner
  ])

  if (!geoData || !localizedEntries.length) return null

  const renderBannerMessage = () => {
    if (!isUserCountrySupported) {
      return (
        <span data-testid='unsupportedCountryBannerMessage'>
          {t('unsupportedCountryBannerMessage', {
            countryCode: `${activeSite?.siteName}`,
            defaultValue: `This content isn't available in your region. You're viewing the ${activeSite?.siteName} version of this page.`
          })}
        </span>
      )
    }
    if (!isUserLangSupported) {
      return (
        <span data-testid='unsupportedLanguageBannerMessage'>
          {t('unsupportedLanguageBannerMessage', {
            language: `${activeSite?.languageName}`,
            defaultValue: `This page is not available in your preferred language. Showing ${activeSite?.languageName} instead.`
          })}
        </span>
      )
    }
  }

  const handleClose = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    event.preventDefault()
    setShowRegionBanner(false)
    setActiveRegionSwitcherInfoBanner(false)
    sessionStorage.setItem('regionBannerClosed', 'true')
  }
  return (
    <>
      {showRegionBanner ? (
        <div
          className='my-2 overflow-hidden'
          data-testid='RegionSwitcherInfoBanner'
        >
          <div className='ds-wrapper'>
            <div className='ds-container'>
              <div className='relative bg-gray-0 min-h-[40px] text-base rounded-lg'>
                <div className='py-2 md:flex md:justify-center md:items-center md:h-full'>
                  <span className='flex justify-center md:items-start md:items-center md:gap-1 md:text-left'>
                    <IconWarningOutline height='18' width='18' />
                  </span>
                  <span className='flex justify-center text-center md:items-start md:pl-1'>
                    <span className='w-[280px] sm:w-[500px] md:w-full'>
                      <strong className='font-sansPro font-semibold text-black tracking-[.2px]'>
                        {renderBannerMessage()}
                      </strong>
                    </span>
                  </span>
                </div>
                <button
                  className='absolute top-1 right-1'
                  onClick={handleClose}
                >
                  <CloseIcon height='30' width='30' />
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default RegionSwitcherInfoBanner
